/**
 * Header notifications bell: unread badge plus a dropdown of the latest
 * notifications. Shares the ['notifications-unread-count'] query with the
 * rest of the app; the list itself is fetched when the dropdown opens.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { SpinnerIcon } from './icons';

interface NotificationItem {
  id: string;
  notification_type: string;
  title: string;
  message?: string;
  is_read: boolean;
  created_at: string;
}

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

const NotificationsBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: countData } = useQuery(
    ['notifications-unread-count'],
    async () => {
      const response = await api.get('/notifications/unread-count');
      return response.data?.data;
    },
    { staleTime: 30 * 1000, refetchInterval: 60 * 1000, retry: 1 },
  );
  const unreadCount = countData?.unreadCount || 0;

  const { data: notifications, isLoading } = useQuery(
    ['notifications-bell-list'],
    async (): Promise<NotificationItem[]> => {
      const response = await api.get('/notifications?limit=8&offset=0');
      return response.data?.data?.notifications ?? [];
    },
    { enabled: open, staleTime: 30 * 1000 },
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-bell-list'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => api.put(`/notifications/${notificationId}/read`),
    onSuccess: invalidate,
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: invalidate,
  });

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex items-center gap-0.5 text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
        }
      >
        <span className="relative">
          <Bell className="w-6 h-6" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-error-500 text-xs text-white rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center"
              aria-hidden="true"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
        <ChevronDown className="w-3 h-3" aria-hidden="true" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-xl border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-secondary shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border-light dark:border-border-dark-hairline">
            <span className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isLoading}
                className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:text-primary-700 disabled:opacity-60"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <SpinnerIcon className="w-5 h-5 animate-spin text-primary-400" aria-hidden="true" />
              </div>
            ) : (notifications ?? []).length === 0 ? (
              <p className="px-3.5 py-8 text-sm text-text-secondary dark:text-text-dark-secondary text-center">
                You're all caught up.
              </p>
            ) : (
              (notifications ?? []).map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.is_read) markReadMutation.mutate(n.id);
                  }}
                  className={`w-full text-left px-3.5 py-2.5 border-b border-border-light dark:border-border-dark-hairline last:border-b-0 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary ${
                    n.is_read ? 'opacity-70' : ''
                  }`}
                >
                  <span className="flex items-start gap-2">
                    {!n.is_read && (
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-none"
                        aria-hidden="true"
                      />
                    )}
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-text-primary dark:text-text-dark-primary leading-snug">
                        {n.title}
                      </span>
                      {n.message && (
                        <span className="block text-xs text-text-secondary dark:text-text-dark-secondary truncate">
                          {n.message}
                        </span>
                      )}
                      <span className="block text-[11px] text-slate-400 dark:text-text-dark-tertiary mt-0.5">
                        {relativeTime(n.created_at)}
                      </span>
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
